import { useCallback, useEffect, useState } from 'react';
import { Divider, Descriptions, Input, Select, Radio, Flex, Checkbox, Space } from 'antd';
import { BaseContainer } from 'components/containers';
import { Editor, EditButton, ListButton, DataGrid, DeleteButton } from 'components/elements';
import { Attachment, Upload } from 'pages/common';
import { getBoardTaskDetailAPI, putBoardTaskAPI, getBoardTypeAPI } from 'api/board';
import { boardAuthOptions, boardTaskTypeOptions, productDisplayStatusOptions } from 'data/options';
import { objToLowerCase } from 'utils/utils';
import { useNavigate, useParams } from 'react-router-dom';
import { URL_ADMIN } from 'routes/AdminRoute';
import { URL_BOARD_TASK, URL_BOARD_TASK_DETAIL } from 'routes/CommonRoute';
import { IconPaperClip } from 'components/assets/icons';
import { BaseSection } from 'components/layouts';
import { CloseSquareOutlined } from '@ant-design/icons';

const BoardTaskEdit = ({ board_type }) => {
  const navigate = useNavigate();
  const { no } = useParams();

  const [options, setOptions] = useState(null);
  const [value, setValue] = useState({
    read_authority: '1',
    display_yn: 1,
    board_type: board_type || '1',
    fixing_top_yn: 0
  });
  const [loading, setLoading] = useState(false)

  const fnUpload = (v) => {
    const fileName = []
    const filePath = []
    v?.map(it => {
      if (it?.file_path) {
        fileName.push(it?.origin_file_name)
        filePath.push(it?.file_path)
      }
    })
    setValue({
      ...value,
      addFileName: fileName,
      addFilePath: filePath
    })
  }

  const fnBoardType = async () => {
    try {
      const { data } = await getBoardTypeAPI()
      const { data_list } = data
      const boardType = data_list?.map((it) => ({ label: it?.BOARD_TYPE_NAME, value: it?.BOARD_TYPE }))
      setOptions({ ...options, boardType })
    } catch (err) {
      console.error(err)
    }
  };

  const fnGetDetail = async () => {
    try {
      const { data } = await getBoardTaskDetailAPI(no)
      const { data_info } = data
      setValue({ ...objToLowerCase(data_info) })
    } catch (err) {
      console.error(err)
    }
  };

  const fnUpdate = async () => {
    setLoading(true)
    try {
      if (!value?.title) {
        alert('제목을 입력해주세요.');
        return false;
      }
      if (!value?.content) {
        alert('내용을 입력해주세요.');
        return false;
      }
      const data = {
        ...value,
        file_list: {
          ...value?.file_list,
          ATTACHED_FILE: value?.addFileName ? (value?.file_list?.ATTACHED_FILE || []).concat(value?.addFileName) : value?.file_list?.ATTACHED_FILE,
          ATTACHED_FILE_PATH: value?.addFilePath ? (value?.file_list?.ATTACHED_FILE_PATH || []).concat(value?.addFilePath) : value?.file_list?.ATTACHED_FILE_PATH
        }
      };
      delete data.addFileName;
      delete data.addFilePath;

      await putBoardTaskAPI(data)
      alert('수정되었습니다.');
      navigate(`${URL_ADMIN}${URL_BOARD_TASK_DETAIL.replace(':no', no)}`);
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  };

  const fnHandleEdit = useCallback((v) => {
    setValue((value) => ({
      ...value,
      ...v
    }));
  }, []);

  useEffect(() => {
    fnBoardType();
    if (no) fnGetDetail();
  }, [no]);

  const fileColumns = [
    {
      key: '1',
      title: '',
      dataIndex: 'attached_file',
      render: (text, record) => (
        <>
          {record?.file_list?.ATTACHED_FILE_PATH && (
            <Space direction={'vertical'} size={0}>
              {record?.file_list?.ATTACHED_FILE_PATH?.map((item, index) => (
                <Space>
                  <Attachment key={index} label={`${record?.file_list?.ATTACHED_FILE?.[index]}`} icon={<IconPaperClip />} url={item} maxLength={100} />
                  <CloseSquareOutlined onClick={()=> {
                    setValue((prev) => ({
                      ...prev,
                      file_list: {
                        ATTACHED_FILE: prev.file_list.ATTACHED_FILE?.filter((_, i) => i !== index),
                        ATTACHED_FILE_PATH: prev.file_list.ATTACHED_FILE_PATH?.filter((_, i) => i !== index),
                      }
                    }));
                    } }/>
                </Space>
              ))}
            </Space>
          )}
        </>
      )
    }
  ];

  const items = [
    { key: '1', span: 2, label: '문서번호', children: value?.idx },
    { key: '2', label: <span className='text-require'>열람권한</span>, children: <Radio.Group value={value?.read_authority} options={boardAuthOptions} onChange={(e) => fnHandleEdit({ read_authority: e.target.value })} /> },
    { key: '3', label: <span className='text-require'>노출여부</span>, children: <Radio.Group value={value?.display_yn} options={productDisplayStatusOptions} onChange={(e) => fnHandleEdit({ display_yn: e.target.value })} /> },
    { key: '4', label: <span className='text-require'>카테고리</span>, children: <Select value={value?.board_type} style={{ width: '20rem' }} options={boardTaskTypeOptions} onSelect={(v) => fnHandleEdit({ board_type: v })} /> },
    { key: '5', label: '상단고정', children: <Checkbox checked={value?.fixing_top_yn} onChange={(e) => fnHandleEdit({ fixing_top_yn: e.target.checked ? 1 : 0 })} /> },
    { key: '6', span: 2, label: <span className='text-require'>제목</span>, children: <Input value={value?.title} onChange={(e) => fnHandleEdit({ title: e.target.value })} /> },
    {
      key: '7',
      label: '첨부파일',
      children: (
        <Flex vertical={true} gap="4px">
          {value?.file_list?.ATTACHED_FILE_PATH && <DataGrid showHeader={false} bordered={false} columns={fileColumns} data={[value]} />}
          <Upload inputSize={'300px'} data={[]} onChange={fnUpload}/>
        </Flex>
      )
    }
  ];

  return (
    <BaseSection>
      <BaseContainer>
        <Descriptions bordered column={2} labelStyle={{ width: '16rem' }} contentStyle={{ textAlign: 'left' }} items={no ? items : items?.slice(1)} />
        <Divider className="divider-small" />
        <Editor data={value?.content} onChange={v => setValue({...value, content: v})} />
        <Divider className="divider" />

        <Flex justify={'end'} gap={3}>
          <EditButton loading={loading} label={'수정하기'} onClick={fnUpdate} />
          <ListButton onClick={() => navigate(`${URL_ADMIN}${URL_BOARD_TASK}`)} />
        </Flex>
      </BaseContainer>
    </BaseSection>
  );
};

export default BoardTaskEdit;
